"use client";
import { useCallback, useEffect, useState } from "react";

/**
 * Type definition for the CustomEvent detail,
 * which is expected to contain the variant string.
 */
interface VariantEventDetail {
  variant: string;
}

/**
 * Type definition for the CustomEvent used by VWO.
 * It extends the base CustomEvent with a specific detail structure.
 */
interface VariantCustomEvent extends CustomEvent {
  detail: VariantEventDetail;
}

/**
 * useVariant hook for fetching A/B test variant data, typically from VWO.
 *
 * usage in code:
 * const variant = useVariant("redesignPdpTest")
 * returns value from VWO variant
 *
 * usage in VWO:
 * var event = new CustomEvent("redesignPdpTest", {
 * detail: {variant: "a"}
 * });
 * (window as any).redesignPdpTest = "a"; // Must assert 'any' or extend Window interface
 * window.dispatchEvent(event);
 *
 * @param type The name of the A/B test (used for window property and event name).
 * @param defaultValue The default variant value to use before a specific variant is determined.
 * @param once If true, the event listener will be removed after the first event, and
 * if a value is found on window on mount, it will exit early.
 * @returns The determined variant string.
 */
export const useVariant = (
  type: string = "redesignPdpTest",
  defaultValue: string = "control",
  once: boolean = true
): string => {
  /*
   * Use 'defaultValue' to ensure consistent rendering between server and client during hydration.
   * Reference dynamic values (like from 'window') inside useEffect, which only runs after the component mounts.
   */
  const [variant, setVariant] = useState<string>(defaultValue);

  // Type the event as VariantCustomEvent
  const handler = useCallback(
    (event: VariantCustomEvent | Event) => {
      /*
       * Type assertion is needed because the 'event' argument is typed as a generic 'Event'
       * in 'window.addEventListener' by default, even if we expect a 'CustomEvent'.
       */
      const customEvent = event as VariantCustomEvent;
      setVariant(customEvent?.detail?.variant || defaultValue);
    },
    [defaultValue]
  );

  useEffect(() => {
    /*
     * Extend the Window interface to include a string index signature for VWO test names
     * or use type assertion when accessing window[type].
     */
    const win = window as typeof window & { [key: string]: string };

    // Check for pre-existing value on mount
    if (typeof window !== "undefined" && win[type]) {
      setVariant(win[type]);
      // exit if value found in window and `once` is true
      if (once) {
        return;
      }
    }

    /*
     * Type the event listener to accept either the specific custom event or a generic Event.
     * The 'handler' expects 'VariantCustomEvent' internally.
     */
    window.addEventListener(type, handler as EventListener, { once });

    return () => {
      // The `handler` is cast to `EventListener` for `removeEventListener`
      window.removeEventListener(type, handler as EventListener);
    };
  }, [type, handler, once]); // Added `once` to dependency array for correctness

  return variant;
};
